import type { MutationFunction } from '@apollo/client';

import type {
  TargetingRuleInput,
  UpdateAdsPageAdMutation,
  UpdateAdsPageAdMutationVariables,
  UpdateAdsPageWithAssetMutation,
  UpdateAdsPageWithAssetMutationVariables,
  UpdateAdWithBatchAssetsMutation,
  UpdateAdWithBatchAssetsMutationVariables,
  UpdateAdWithGatewayAssetsMutation,
  UpdateAdWithGatewayAssetsMutationVariables,
} from '../../../../../apis/graphql';
import type { AdFormValues } from '../../../../../common/AdForm/adFormik';
import type { AssetCountryCodesInput } from '../../util/buildAssetCountryCodesInput';
import type { GatewayGoAssetCountryCodesInput } from '../../util/buildGatewayAssetCountryCodesInput';
import { constructUpdateAdInput } from './constructUpdateAdInput';
import { getAssetId } from './getAssetId';

type Mutations = {
  updateAd: MutationFunction<UpdateAdsPageAdMutation>;
  updateAdWithAsset: MutationFunction<UpdateAdsPageWithAssetMutation>;
  updateAdWithBatchAssets: MutationFunction<UpdateAdWithBatchAssetsMutation>;
  updateAdWithGatewayAssets: MutationFunction<UpdateAdWithGatewayAssetsMutation>;
};

type WithoutAsset = { mutation: Mutations['updateAd']; variables: UpdateAdsPageAdMutationVariables };
type WithAsset = { mutation: Mutations['updateAdWithAsset']; variables: UpdateAdsPageWithAssetMutationVariables };
type WithBatchAssets = {
  mutation: Mutations['updateAdWithBatchAssets'];
  variables: UpdateAdWithBatchAssetsMutationVariables;
};
type WithGatewayAssets = {
  mutation: Mutations['updateAdWithGatewayAssets'];
  variables: UpdateAdWithGatewayAssetsMutationVariables;
};

export function getUpdateMutation(
  mutations: Mutations,
  adFormValues: AdFormValues,
  assetCountryCodesInput?: AssetCountryCodesInput | GatewayGoAssetCountryCodesInput,
  targetingRule?: TargetingRuleInput | null
): WithoutAsset | WithAsset | WithBatchAssets | WithGatewayAssets {
  const assetId = getAssetId(adFormValues.creative.type, adFormValues);
  const creativeLibraryId = adFormValues.creative.creativeLibraryId ?? '';

  // The Ad has a GatewayGo Asset
  if (
    (assetCountryCodesInput as GatewayGoAssetCountryCodesInput)?.displayCountryCodes &&
    (assetCountryCodesInput as GatewayGoAssetCountryCodesInput)?.videoCountryCodes
  ) {
    const withGatewayAssets: WithGatewayAssets = {
      mutation: mutations.updateAdWithGatewayAssets,
      variables: {
        adId: adFormValues.id || '',
        updateAdInput: constructUpdateAdInput(adFormValues),
        targetingRule: targetingRule,
        displayAssetId: adFormValues.creative.image?.id || '',
        displayAssetCountryCodes: (assetCountryCodesInput as GatewayGoAssetCountryCodesInput).displayCountryCodes,
        videoAssetId: adFormValues.creative.video?.id || '',
        videoAssetCountryCodes: (assetCountryCodesInput as GatewayGoAssetCountryCodesInput).videoCountryCodes,
        creativeLibraryId,
      },
    };
    return withGatewayAssets;
  }

  // The Ad has one or more standard Assets for mutation
  if (assetId && assetCountryCodesInput) {
    // The Ad has a single Asset
    if (typeof assetId === 'string') {
      const withAsset: WithAsset = {
        mutation: mutations.updateAdWithAsset,
        variables: {
          adId: adFormValues.id || '',
          updateAdInput: constructUpdateAdInput(adFormValues),
          assetId: assetId || '',
          countryCodes: (assetCountryCodesInput as AssetCountryCodesInput).countryCodes,
          targetingRule: targetingRule,
          creativeLibraryId,
        },
      };
      return withAsset;
    }

    // The Ad has a batch list of Assets
    if (Array.isArray(assetId)) {
      const withBatchAssets: WithBatchAssets = {
        mutation: mutations.updateAdWithBatchAssets,
        variables: {
          adId: adFormValues.id || '',
          updateAdInput: constructUpdateAdInput(adFormValues),
          assetIdList: assetId || '',
          countryCodes: (assetCountryCodesInput as AssetCountryCodesInput).countryCodes,
          targetingRule: targetingRule,
          creativeLibraryId,
        },
      };
      return withBatchAssets;
    }
  }

  // No Asset condition has been met; we're updating the Ad only
  const withoutAsset: WithoutAsset = {
    mutation: mutations.updateAd,
    variables: {
      adId: adFormValues.id || '',
      updateAdInput: constructUpdateAdInput(adFormValues),
      targetingRule: targetingRule,
    },
  };
  return withoutAsset;
}
